/** One reward milestone from `GET /referral/milestones` (backend config). */
export interface MilestoneStep {
  required_score: number
  /** Server-side delivery id; the client renders reward visuals purely by
   *  milestone position and ignores this (see reward-visuals). */
  reward_item_type_id: number
}

/** One claimed row from `GET /users/:id/referral/rewards` — a row exists
 *  only for a claimed & delivered milestone. */
export interface ClaimedReward {
  required_score: number
  claimed_at: string
}

export type MilestoneState = 'locked' | 'claimable' | 'claimed'

export interface MilestoneView {
  requiredScore: number
  /** Position in the score-ascending milestone list — the key into the
   *  client's hardcoded reward visuals (see reward-visuals). */
  index: number
  state: MilestoneState
}

/**
 * The rewards-log update applied client-side after a successful claim
 * (present-in-log ≡ claimed — see GLOSSARY.md "Milestone claim state").
 * Idempotent: a milestone already in the log is left untouched.
 */
export function withClaimedReward(
  claimed: ClaimedReward[],
  reward: ClaimedReward,
): ClaimedReward[] {
  const alreadyClaimed = claimed.some(
    (entry) => entry.required_score === reward.required_score,
  )
  return alreadyClaimed ? claimed : [...claimed, reward]
}

/**
 * Composes each milestone's claim state client-side from the three read
 * sources — the backend returns only raw data (see GLOSSARY.md "Milestone
 * claim state").
 */
export function deriveMilestoneViews(
  steps: MilestoneStep[],
  accumulatedScore: number,
  claimed: ClaimedReward[],
): MilestoneView[] {
  const ordered = [...steps].sort((a, b) => a.required_score - b.required_score)
  return ordered.map((step, index) => {
    const isClaimed = claimed.some(
      (reward) => reward.required_score === step.required_score,
    )
    const reached = accumulatedScore >= step.required_score
    const state: MilestoneState = isClaimed
      ? 'claimed'
      : reached
        ? 'claimable'
        : 'locked'
    return {
      requiredScore: step.required_score,
      index,
      state,
    }
  })
}
